import { bindFirst } from "@udecode/utils";
import { Editor, Element, Location, Node, Operation, Path, Point, PointRef, Range, RangeRef, Span, Text, Transforms, above, addMark, after, apply, before, collapse, createEditor as createEditor$1, deleteBackward, deleteForward, deleteFragment, deselect, edges, elementReadOnly, end, first, fragment, getDirtyPaths, getFragment, getVoid, hasBlocks, hasInlines, hasPath, hasTexts, insertBreak, insertFragment, insertNodes, isBlock, isEdge, isEditor, isEmpty, isEnd, isNormalizing, isStart, last, leaf, levels, liftNodes, marks, move, moveNodes, node, normalize, normalizeNode, parent, path, pathRef, pathRefs, point, pointRef, pointRefs, positions, range, rangeRef, rangeRefs, removeMark, removeNodes, select, setNodes, setNormalizing, setPoint, setSelection, shouldNormalize, splitNodes, start, string, unhangRange, unsetNodes, unwrapNodes, withoutNormalizing, wrapNodes } from "slate";
import { CAN_USE_DOM, DOMEditor, HAS_BEFORE_INPUT_SUPPORT, IS_ANDROID, IS_CHROME, IS_FIREFOX, IS_FIREFOX_LEGACY, IS_IOS, IS_UC_MOBILE, IS_WEBKIT, IS_WECHATBROWSER, TRIPLE_CLICK, applyStringDiff, getActiveElement, getDefaultView, getSelection, hasShadowRoot, isAfter, isBefore, isDOMElement, isDOMNode, isDOMSelection, isElementDecorationsEqual, isPlainTextOnlyPaste, isTextDecorationsEqual, isTrackedMutation, mergeStringDiffs, normalizeDOMPoint, normalizePoint, normalizeRange, normalizeStringDiff, targetRange, verifyDiffState, withDOM } from "slate-dom";
import isPlainObject from "lodash/isPlainObject.js";
import castArray from "lodash/castArray.js";
import map from "lodash/map.js";
import { isPlainObject as isPlainObject$1 } from "is-plain-object";

//#region src/interfaces/element.ts
/** Element retrieval and check methods. */
const ElementApi = { ...Element };

//#endregion
//#region src/interfaces/path.ts
/** Path retrieval, check and transform methods. */
const PathApi = {
	...Path,
	child: (path$2, index) => path$2.concat([index]),
	firstChild: (path$2) => PathApi.child(path$2, 0),
	lastIndex: (path$2) => path$2.at(-1) ?? -1,
	next: (path$2) => {
		try {
			return Path.next(path$2);
		} catch {
			return path$2;
		}
	},
	parent: (path$2) => {
		try {
			return Path.parent(path$2);
		} catch {
			return path$2;
		}
	},
	previous: (path$2) => {
		if (path$2.length === 0) return;
		const last$2 = path$2.at(-1);
		if (last$2 <= 0) return;
		return path$2.slice(0, -1).concat(last$2 - 1);
	}
};

//#endregion
//#region src/interfaces/location-ref.ts
const PathRefApi = { transform(ref, op) {
	const { affinity, current } = ref;
	if (current == null) return;
	const path$2 = PathApi.transform(current, op, { affinity });
	ref.current = path$2;
	if (path$2 == null) ref.unref();
} };
const PointRefApi = PointRef;
const RangeRefApi = RangeRef;

//#endregion
//#region src/internal/editor-extension/node-extension.ts
const NodeExtension = {
	*children(root, path$2, options = {}) {
		const { from, reverse = false, to } = options;
		const ancestor = NodeApi.ancestor(root, path$2);
		if (!ancestor) return;
		const { children } = ancestor;
		let index = reverse ? children.length - 1 : 0;
		const endIndex = to ?? (reverse ? 0 : children.length);
		if (from !== void 0) index = from;
		while (reverse ? index >= endIndex : index < endIndex) {
			yield [NodeApi.child(ancestor, index), path$2.concat(index)];
			index = reverse ? index - 1 : index + 1;
		}
	},
	firstChild(root, path$2) {
		return NodeApi.children(root, path$2).next().value;
	},
	firstText(root, options) {
		return NodeApi.texts(root, options).next().value;
	},
	isEditor: (value) => isEditor(value),
	isLastChild(root, path$2) {
		if (path$2.length === 0) return false;
		const parent$2 = NodeApi.parent(root, path$2);
		if (!parent$2) return false;
		return path$2.at(-1) === parent$2.children.length - 1;
	},
	lastChild(root, path$2) {
		return NodeApi.children(root, path$2, { reverse: true }).next().value;
	}
};

//#endregion
//#region src/interfaces/text.ts
/** Text retrieval and check methods. */
const TextApi = Text;

//#endregion
//#region src/interfaces/node.ts
const NodeApi = {
	...Node,
	isAncestor: ElementApi.isAncestor,
	ancestor: (...args) => {
		try {
			return Node.ancestor(...args);
		} catch {}
	},
	common: (...args) => {
		try {
			return Node.common(...args);
		} catch {}
	},
	descendant: (...args) => {
		try {
			return Node.descendant(...args);
		} catch {}
	},
	first: (...args) => {
		try {
			return Node.first(...args);
		} catch {}
	},
	fragment: (...args) => {
		try {
			const fragment$1 = Node.fragment;
			return fragment$1(...args);
		} catch {
			return [];
		}
	},
	get: (...args) => {
		try {
			return Node.get(...args);
		} catch {}
	},
	hasSingleChild: (node$2) => {
		if (TextApi.isText(node$2)) return true;
		return node$2.children.length === 1 && NodeApi.hasSingleChild(node$2.children[0]);
	},
	isDescendant: (node$2) => ElementApi.isElement(node$2) || TextApi.isText(node$2),
	last: (...args) => {
		try {
			return Node.last(...args);
		} catch {}
	},
	leaf: (...args) => {
		try {
			return Node.leaf(...args);
		} catch {}
	},
	parent: (...args) => {
		try {
			return Node.parent(...args);
		} catch {}
	},
	...NodeExtension
};

//#endregion
//#region src/interfaces/location.ts
/** Location check methods. */
const LocationApi = {
	...Location,
	isAt: (value) => LocationApi.isLocation(value) || NodeApi.isNode(value)
};
const SpanApi = Span;

//#endregion
//#region src/interfaces/operation.ts
/** Operation manipulation and check methods. */
const OperationApi = Operation;

//#endregion
//#region src/interfaces/range.ts
const RangeApi = {
	...Range,
	contains: (range$2, target) => {
		const [targetStart, targetEnd] = RangeApi.edges(target);
		return RangeApi.includes(range$2, targetStart) && RangeApi.includes(range$2, targetEnd);
	},
	isCollapsed: (range$2) => !!range$2 && Range.isCollapsed(range$2),
	isExpanded: (range$2) => !!range$2 && Range.isExpanded(range$2)
};

//#endregion
//#region src/interfaces/point.ts
/** Point retrieval, check and transform methods. */
const PointApi = {
	...Point,
	get: (at, { focus: focus$1 } = {}) => {
		let point$1;
		if (RangeApi.isRange(at)) point$1 = focus$1 ? at.focus : at.anchor;
		if (PointApi.isPoint(at)) point$1 = at;
		if (PathApi.isPath(at)) point$1 = {
			offset: 0,
			path: at
		};
		return point$1;
	}
};

//#endregion
//#region src/internal/dom-editor/blur.ts
const blur = (editor) => DOMEditor.blur(editor);

//#endregion
//#region src/internal/dom-editor/deselectDOM.ts
const deselectDOM = (editor) => DOMEditor.deselect(editor);

//#endregion
//#region src/internal/dom-editor/findDocumentOrShadowRoot.ts
const findDocumentOrShadowRoot = (editor) => {
	try {
		return DOMEditor.findDocumentOrShadowRoot(editor);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/findEventRange.ts
const findEventRange = (editor, event) => {
	try {
		return DOMEditor.findEventRange(editor, event);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/findKey.ts
const findKey = (editor, node$2) => {
	try {
		return DOMEditor.findKey(editor, node$2);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/findPath.ts
const findPath = (editor, node$2, options) => {
	const findNodePath = () => {
		return editor.api.node({
			...options,
			at: [],
			match: (n$1) => n$1 === node$2
		})?.[1];
	};
	if (options) return findNodePath();
	try {
		return DOMEditor.findPath(editor, node$2);
	} catch {
		return findNodePath();
	}
};

//#endregion
//#region src/internal/dom-editor/focus.ts
const focus = (editor, { at, edge, retries = 5 } = {}) => {
	const reselect = (at$1) => {
		editor.tf.withoutNormalizing(() => {
			editor.tf.deselect();
			editor.tf.select(at$1);
		});
	};
	if (edge) {
		const target = edge === "startEditor" || edge === "endEditor" ? [] : at ?? editor.selection;
		if (target) reselect(edge === "start" ? editor.api.start(target) : editor.api.end(target));
	} else if (at) reselect(at);
	try {
		DOMEditor.focus(editor, { retries });
	} catch (error) {
		console.error(error);
	}
};

//#endregion
//#region src/internal/dom-editor/getWindow.ts
const getWindow = (editor) => {
	try {
		return DOMEditor.getWindow(editor);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/hasDOMNode.ts
const hasDOMNode = (editor, target, options) => {
	try {
		return DOMEditor.hasDOMNode(editor, target, options);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/hasEditableTarget.ts
const hasEditableTarget = (editor, target) => {
	try {
		return DOMEditor.hasEditableTarget(editor, target);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/hasRange.ts
const hasRange = (editor, range$2) => {
	try {
		return DOMEditor.hasRange(editor, range$2);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/hasSelectableTarget.ts
const hasSelectableTarget = (editor, target) => {
	try {
		return DOMEditor.hasSelectableTarget(editor, target);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/hasTarget.ts
const hasTarget = (editor, target) => {
	try {
		return DOMEditor.hasTarget(editor, target);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/isComposing.ts
const isComposing = (editor) => DOMEditor.isComposing(editor);

//#endregion
//#region src/internal/dom-editor/isFocused.ts
const isFocused = (editor) => DOMEditor.isFocused(editor);

//#endregion
//#region src/internal/dom-editor/isReadOnly.ts
const isReadOnly = (editor) => DOMEditor.isReadOnly(editor);

//#endregion
//#region src/internal/dom-editor/isTargetInsideNonReadonlyVoid.ts
const isTargetInsideNonReadonlyVoid = (editor, target) => {
	try {
		return DOMEditor.isTargetInsideNonReadonlyVoid(editor, target);
	} catch {}
	return false;
};

//#endregion
//#region src/internal/dom-editor/toDOMNode.ts
const toDOMNode = (editor, node$2) => {
	try {
		return DOMEditor.toDOMNode(editor, node$2);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/toDOMPoint.ts
const toDOMPoint = (editor, point$1) => {
	try {
		return DOMEditor.toDOMPoint(editor, point$1);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/toDOMRange.ts
const toDOMRange = (editor, range$2) => {
	try {
		return DOMEditor.toDOMRange(editor, range$2);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/toSlateNode.ts
const toSlateNode = (editor, domNode) => {
	try {
		return DOMEditor.toSlateNode(editor, domNode);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/toSlatePoint.ts
const toSlatePoint = (editor, domPoint, options) => {
	try {
		return DOMEditor.toSlatePoint(editor, domPoint, options);
	} catch {}
};

//#endregion
//#region src/internal/dom-editor/toSlateRange.ts
const toSlateRange = (editor, domRange, options) => {
	try {
		return DOMEditor.toSlateRange(editor, domRange, options);
	} catch {}
};

//#endregion
//#region src/utils/getAt.ts
const getAt = (editor, at) => {
	if (at && isPlainObject(at) && NodeApi.isNode(at)) return editor.api.findPath(at);
	return at ?? void 0;
};

//#endregion
//#region src/utils/match.ts
function castArray$2(value) {
	return Array.isArray(value) ? value : [value];
}
/**
* Match the object with a predicate object or function. If predicate is:
*
* - Object: every predicate key/value should be in obj.
* - Function: it should return true.
*/
const match = (obj, path$2, predicate) => {
	if (!predicate) return true;
	if (typeof predicate === "object") return Object.entries(predicate).every(([key, value]) => {
		return castArray$2(value).includes(obj[key]);
	});
	return predicate(obj, path$2);
};
const getMatch = (editor, { id, block: block$1, empty, match: matchObjOrFn, text } = {}) => {
	let hasMatch = false;
	let matchFn = () => true;
	if (text !== void 0) {
		hasMatch = true;
		matchFn = combineMatch(matchFn, (n$1) => TextApi.isText(n$1) === text);
	}
	if (empty !== void 0) {
		hasMatch = true;
		matchFn = combineMatch(matchFn, (n$1) => TextApi.isText(n$1) ? n$1.text.length > 0 === !empty : editor.api.isEmpty(n$1) === empty);
	}
	if (block$1 !== void 0) {
		hasMatch = true;
		matchFn = combineMatch(matchFn, (n$1) => editor.api.isBlock(n$1) === block$1);
	}
	if (id !== void 0) {
		hasMatch = true;
		matchFn = combineMatch(matchFn, (n$1) => id === true && !!n$1.id || n$1.id === id);
	}
	if (typeof matchObjOrFn === "object") {
		hasMatch = true;
		matchFn = combineMatch(matchFn, (n$1, p) => match(n$1, p, matchObjOrFn));
	} else if (typeof matchObjOrFn === "function") {
		hasMatch = true;
		matchFn = combineMatch(matchFn, matchObjOrFn);
	}
	return hasMatch ? matchFn : void 0;
};
/**
* Extended query options for slate queries:
*
* - `match` can be an object predicate where one of the values should include the
*   node value. Example: { type: ['1', '2'] } will match the nodes having one
*   of these 2 types.
*/
const getQueryOptions = (editor, { id, empty, match: match$1, text, ...options } = {}) => {
	const { at, block: block$1 } = options;
	return {
		...options,
		at: getAt(editor, at),
		match: getMatch(editor, {
			id,
			block: block$1,
			empty,
			match: match$1,
			text
		})
	};
};
const combineMatch = (match1, match2) => (node$2, path$2) => match1(node$2, path$2) && (!match2 || match2(node$2, path$2));
/** Combine two match predicates into one. */
const combineMatchOptions = (editor, match1, options) => (node$2, path$2) => {
	const match2 = getMatch(editor, options);
	return (!match1 || match1(node$2, path$2)) && (!match2 || match2(node$2, path$2));
};
/** Used by liftNodes, moveNodes, removeNodes, setNodes, unwrapNodes */
const combineTransformMatchOptions = (editor, match1, options) => {
	return (node$2, path$2) => {
		const getDefaultMatch = () => {
			if (!options?.match) {
				if (options?.at && PathApi.isPath(options.at)) {
					const [node$3] = editor.api.node(options.at) ?? [];
					return (n$1) => n$1 === node$3;
				}
				return (n$1) => ElementApi.isElement(n$1) && editor.api.isBlock(n$1);
			}
			return getMatch(editor, options);
		};
		const defaultMatch = getDefaultMatch();
		return (!match1 || match1(node$2, path$2)) && (!defaultMatch || defaultMatch(node$2, path$2));
	};
};

//#endregion
//#region src/internal/editor/above.ts
const above$1 = (editor, options) => {
	try {
		return above(editor, getQueryOptions(editor, options));
	} catch {
		return;
	}
};

//#endregion
//#region src/internal/editor/addMark.ts
const addMark$1 = (editor, key, value) => addMark(editor, key, value);

//#endregion
//#region src/internal/editor/createPathRef.ts
const createPathRef = (editor, at, options) => pathRef(editor, at, options);

//#endregion
//#region src/internal/editor/createPointRef.ts
const createPointRef = (editor, point$1, options) => pointRef(editor, point$1, options);

//#endregion
//#region src/internal/editor/createRangeRef.ts
const createRangeRef = (editor, range$2, options) => rangeRef(editor, range$2, options);

//#endregion
//#region src/internal/editor/deleteBackward.ts
const deleteBackward$1 = (editor, unit = "character") => {
	deleteBackward(editor, unit);
};

//#endregion
//#region src/internal/editor/deleteForward.ts
const deleteForward$1 = (editor, unit = "character") => {
	deleteForward(editor, unit);
};

//#endregion
//#region src/internal/editor/deleteFragment.ts
const deleteFragment$1 = (editor, options) => deleteFragment(editor, options);

//#endregion
//#region src/utils/assignLegacyTransforms.ts
const LEGACY_TRANSFORMS = new Set([
	"addMark",
	"apply",
	"blur",
	"collapse",
	"delete",
	"deleteBackward",
	"deleteForward",
	"deleteFragment",
	"deselect",
	"deselectDOM",
	"focus",
	"insertBreak",
	"insertData",
	"insertFragment",
	"insertFragmentData",
	"insertNode",
	"insertNodes",
	"insertSoftBreak",
	"insertText",
	"insertTextData",
	"liftNodes",
	"mergeNodes",
	"move",
	"moveNodes",
	"normalize",
	"normalizeNode",
	"redo",
	"removeMark",
	"removeNodes",
	"select",
	"setFragmentData",
	"setNodes",
	"setPoint",
	"setSelection",
	"setSplittingOnce",
	"splitNodes",
	"undo",
	"unsetNodes",
	"unwrapNodes",
	"withMerging",
	"withNewBatch",
	"withoutMerging",
	"withoutNormalizing",
	"withoutSaving",
	"wrapNodes",
	"writeHistory"
]);
const LEGACY_API = new Set([
	"above",
	"after",
	"before",
	"edges",
	"elementReadOnly",
	"end",
	"findDocumentOrShadowRoot",
	"findEventRange",
	"findKey",
	"findPath",
	"first",
	"fragment",
	"getDirtyPaths",
	"getFragment",
	"getMarks",
	"getWindow",
	"hasBlocks",
	"hasDOMNode",
	"hasEditableTarget",
	"hasInlines",
	"hasPath",
	"hasRange",
	"hasSelectableTarget",
	"hasTarget",
	"hasTexts",
	"highestBlock",
	"isBlock",
	"isComposing",
	"isEdge",
	"isElementReadOnly",
	"isEmpty",
	"isEnd",
	"isFocused",
	"isInline",
	"isMerging",
	"isNormalizing",
	"isReadOnly",
	"isSaving",
	"isSelectable",
	"isSplittingOnce",
	"isStart",
	"isTargetInsideNonReadonlyVoid",
	"isVoid",
	"last",
	"leaf",
	"levels",
	"markableVoid",
	"next",
	"node",
	"nodes",
	"normalize",
	"onChange",
	"operations",
	"parent",
	"path",
	"pathRef",
	"pathRefs",
	"point",
	"pointRef",
	"pointRefs",
	"positions",
	"previous",
	"range",
	"rangeRef",
	"rangeRefs",
	"selection",
	"setNormalizing",
	"shouldMergeNodes",
	"shouldNormalize",
	"start",
	"string",
	"toDOMNode",
	"toDOMPoint",
	"toDOMRange",
	"toSlateNode",
	"toSlatePoint",
	"toSlateRange",
	"unhangRange",
	"void"
]);
const assignLegacyTransforms = (editor, transforms) => {
	if (!transforms) return;
	const e$2 = editor;
	const legacyTransforms = Object.entries(transforms).reduce((acc, [key, value]) => {
		if (LEGACY_TRANSFORMS.has(key)) acc[key] = value;
		return acc;
	}, {});
	Object.assign(e$2, legacyTransforms);
};
const assignLegacyApi = (editor, api) => {
	if (!api) return;
	const e$2 = editor;
	const legacyApi = Object.entries(api).reduce((acc, [key, value]) => {
		if (LEGACY_API.has(key)) acc[key] = value;
		return acc;
	}, {});
	Object.assign(e$2, legacyApi);
	if (api.marks) e$2.getMarks = api.marks;
};
/**
* Assigns editor's legacy methods to editor.api and editor.tf.
*
* NOTE: can't use yet because of recursion issues
*/
const syncLegacyMethods = (editor) => {
	const e$2 = editor;
	LEGACY_API.forEach((key) => {
		if (e$2[key]) if (key === "getMarks") e$2.api.marks = e$2.getMarks;
		else e$2.api[key] = e$2[key];
	});
	LEGACY_TRANSFORMS.forEach((key) => {
		if (e$2[key]) e$2.tf[key] = e$2[key];
	});
};

//#endregion
//#region src/internal/editor/getEndPoint.ts
const getEndPoint = (editor, at, options = {}) => {
	try {
		if (options.previous) {
			const prevEntry = editor.api.previous({ at: getAt(editor, at) });
			if (!prevEntry) return;
			return end(editor, prevEntry[1]);
		}
		return end(editor, getAt(editor, at));
	} catch {}
};

//#endregion
//#region src/internal/editor/getLeafNode.ts
const getLeafNode = (editor, at, options) => {
	try {
		return leaf(editor, getAt(editor, at), options);
	} catch {}
};

//#endregion
//#region src/internal/editor/getPointAfter.ts
const getPointAfter = (editor, at, options) => {
	try {
		return after(editor, getAt(editor, at), options);
	} catch {}
};

//#endregion
//#region src/internal/editor/getPointBefore.ts
const getPointBefore = (editor, at, options) => {
	if (!options || !options.match && !options.matchString) {
		try {
			return before(editor, getAt(editor, at), options);
		} catch {}
		return;
	}
	const unitOffset = !options.unit || options.unit === "offset";
	const matchStrings = options.matchString ? castArray(options.matchString) : [""];
	const matchByRegex = options.matchByRegex ?? false;
	let point$1;
	matchStrings.some((matchString) => {
		let beforeAt = at;
		let previousBeforePoint = editor.api.point(at, { edge: "end" });
		const stackLength = matchString.length + 1;
		const stack = Array.from({ length: stackLength });
		let count = 0;
		while (true) {
			const beforePoint = before(editor, getAt(editor, beforeAt), options);
			if (!beforePoint) {
				if (options.matchBlockStart) point$1 = previousBeforePoint;
				return false;
			}
			if (editor.api.isAt({
				at: {
					anchor: beforePoint,
					focus: previousBeforePoint
				},
				blocks: true
			})) {
				if (options.matchBlockStart) point$1 = previousBeforePoint;
				return false;
			}
			const beforeString = editor.api.string({
				anchor: beforePoint,
				focus: previousBeforePoint
			});
			let beforeStringToMatch = beforeString;
			if (unitOffset && stackLength) {
				stack.unshift({
					point: beforePoint,
					text: beforeString
				});
				stack.pop();
				beforeStringToMatch = map(stack.slice(0, -1), "text").join("");
			}
			if ((matchByRegex ? !!matchString.match(beforeStringToMatch) : beforeStringToMatch === matchString) || options.match?.({
				at,
				beforePoint,
				beforeString: beforeStringToMatch
			})) {
				if (options.afterMatch) {
					if (stackLength && unitOffset) {
						point$1 = stack.at(-1)?.point;
						return !!point$1;
					}
					point$1 = previousBeforePoint;
					return true;
				}
				point$1 = beforePoint;
				return true;
			}
			previousBeforePoint = beforePoint;
			beforeAt = beforePoint;
			count += 1;
			if (!options.skipInvalid && (!matchString || count >= matchString.length)) return false;
		}
		return false;
	});
	return point$1;
};

//#endregion
//#region src/internal/editor/getStartPoint.ts
const getStartPoint = (editor, at, options = {}) => {
	try {
		if (options.next) {
			const nextEntry = editor.api.next({ at: getAt(editor, at) });
			if (!nextEntry) return;
			return start(editor, nextEntry[1]);
		}
		return start(editor, getAt(editor, at));
	} catch {}
};

//#endregion
//#region src/internal/editor/getVoidNode.ts
const getVoidNode = (editor, options) => getVoid(editor, {
	...options,
	at: getAt(editor, options?.at)
});

//#endregion
//#region src/internal/editor/isBlock.ts
const isBlock$1 = (editor, value) => ElementApi.isElement(value) && isBlock(editor, value);

//#endregion
//#region src/internal/editor/nodes.ts
function* nodes(editor, options = {}) {
	const _options = getQueryOptions(editor, options);
	const { ignoreNonSelectable = false, mode = "all", reverse = false, universal = false, voids = false } = _options;
	const at = getAt(editor, _options.at) ?? editor.selection;
	let match$1 = getMatch(editor, _options);
	if (!match$1) match$1 = () => true;
	if (!at) return;
	let from;
	let to;
	if (SpanApi.isSpan(at)) {
		from = at[0];
		to = at[1];
	} else {
		const first$1 = editor.api.path(at, { edge: "start" });
		const last$2 = editor.api.path(at, { edge: "end" });
		from = reverse ? last$2 : first$1;
		to = reverse ? first$1 : last$2;
		if (!first$1 || !last$2) return;
	}
	const nodeEntries = NodeApi.nodes(editor, {
		from,
		reverse,
		to,
		pass: ([node$2]) => {
			if (!ElementApi.isElement(node$2)) return false;
			if (!voids && (editor.api.isVoid(node$2) || editor.api.isElementReadOnly(node$2))) return true;
			if (ignoreNonSelectable && !editor.api.isSelectable(node$2)) return true;
			return false;
		}
	});
	const matches = [];
	let hit;
	for (const [node$2, path$2] of nodeEntries) {
		if (ignoreNonSelectable && ElementApi.isElement(node$2) && !editor.api.isSelectable(node$2)) continue;
		const isLower = hit && PathApi.compare(path$2, hit[1]) === 0;
		if (mode === "highest" && isLower) continue;
		if (!match$1(node$2, path$2)) {
			if (universal && !isLower && TextApi.isText(node$2)) return;
			continue;
		}
		if (mode === "lowest" && isLower) {
			hit = [node$2, path$2];
			continue;
		}
		const emit = mode === "lowest" ? hit : [node$2, path$2];
		if (emit) if (universal) matches.push(emit);
		else yield emit;
		hit = [node$2, path$2];
	}
	if (mode === "lowest" && hit) if (universal) matches.push(hit);
	else yield hit;
	if (universal) yield* matches;
}

//#endregion
//#region src/internal/editor/withoutNormalizing.ts
const withoutNormalizing$1 = (editor, fn) => {
	let normalized = false;
	withoutNormalizing(editor, () => {
		normalized = !!fn();
	});
	return normalized;
};

//#endregion
//#region src/internal/transforms/select.ts
const select$1 = (editor, target, options = {}) => {
	const { edge, focus: focus$1, next: next$1, previous: previous$1 } = options;
	if (focus$1) editor.tf.focus();
	if (next$1 || previous$1) {
		const at$1 = getAt(editor, target) ?? editor.selection;
		if (!at$1) return;
		const path$2 = editor.api.path(at$1);
		if (!path$2) return;
		const point$1 = previous$1 ? editor.api.end(path$2, { previous: true }) : editor.api.start(path$2, { next: true });
		if (!point$1) return;
		select(editor, point$1);
		return;
	}
	if (edge) {
		const at$1 = getAt(editor, target) ?? editor.selection;
		if (!at$1) return;
		const path$2 = PathApi.isPath(at$1) ? at$1 : editor.api.node({
			at: at$1,
			block: true
		})?.[1];
		if (!path$2) return;
		const point$1 = edge === "end" ? editor.api.end(path$2) : editor.api.start(path$2);
		if (!point$1) return;
		select(editor, point$1);
		return;
	}
	const at = getAt(editor, target);
	if (!at) return;
	select(editor, at);
};

//#endregion
//#region src/utils/deleteMerge.ts
const deleteMerge = (editor, options = {}) => {
	const e$2 = editor;
	withoutNormalizing$1(e$2, () => {
		const { distance = 1, reverse = false, unit = "character", voids = false } = options;
		let { at = e$2.selection, hanging = false } = options;
		if (!at) return;
		if (RangeApi.isRange(at) && RangeApi.isCollapsed(at)) at = at.anchor;
		if (PointApi.isPoint(at)) {
			const furthestVoid = getVoidNode(e$2, {
				at,
				mode: "highest"
			});
			if (!voids && furthestVoid) {
				const [, voidPath] = furthestVoid;
				at = voidPath;
			} else {
				const opts = {
					distance,
					unit
				};
				const target = reverse ? getPointBefore(e$2, at, opts) || getStartPoint(e$2, []) : getPointAfter(e$2, at, opts) || getEndPoint(e$2, []);
				at = {
					anchor: at,
					focus: target
				};
				hanging = true;
			}
		}
		if (PathApi.isPath(at)) {
			e$2.tf.removeNodes({
				at,
				voids
			});
			return;
		}
		if (RangeApi.isCollapsed(at)) return;
		if (!hanging) at = Editor.unhangRange(e$2, at, { voids });
		let [start$1, end$1] = RangeApi.edges(at);
		const startBlock = e$2.api.above({
			at: start$1,
			voids,
			match: (n$1) => isBlock$1(e$2, n$1)
		});
		const endBlock = e$2.api.above({
			at: end$1,
			voids,
			match: (n$1) => isBlock$1(e$2, n$1)
		});
		const isAcrossBlocks = startBlock && endBlock && !PathApi.equals(startBlock[1], endBlock[1]);
		const isSingleText = PathApi.equals(start$1.path, end$1.path);
		const startVoid = voids ? null : getVoidNode(e$2, {
			at: start$1,
			mode: "highest"
		});
		const endVoid = voids ? null : getVoidNode(e$2, {
			at: end$1,
			mode: "highest"
		});
		if (startVoid) {
			const before$1 = getPointBefore(e$2, start$1);
			if (before$1 && startBlock && PathApi.isAncestor(startBlock[1], before$1.path)) start$1 = before$1;
		}
		if (endVoid) {
			const after$1 = getPointAfter(e$2, end$1);
			if (after$1 && endBlock && PathApi.isAncestor(endBlock[1], after$1.path)) end$1 = after$1;
		}
		const matches = [];
		let lastPath;
		const _nodes = nodes(e$2, {
			at,
			voids
		});
		for (const entry of _nodes) {
			const [node$2, path$2] = entry;
			if (lastPath && PathApi.compare(path$2, lastPath) === 0) continue;
			if (!voids && e$2.api.isVoid(node$2) || !PathApi.isCommon(path$2, start$1.path) && !PathApi.isCommon(path$2, end$1.path)) {
				matches.push(entry);
				lastPath = path$2;
			}
		}
		const pathRefs$1 = Array.from(matches, ([, p]) => createPathRef(e$2, p));
		const startRef = createPointRef(e$2, start$1);
		const endRef = createPointRef(e$2, end$1);
		if (!isSingleText && !startVoid) {
			const point$2 = startRef.current;
			const [node$2] = getLeafNode(e$2, point$2);
			const { path: path$2 } = point$2;
			const { offset } = start$1;
			const text = node$2.text.slice(offset);
			e$2.apply({
				offset,
				path: path$2,
				text,
				type: "remove_text"
			});
		}
		for (const pathRef$1 of pathRefs$1) {
			const path$2 = pathRef$1.unref();
			e$2.tf.removeNodes({
				at: path$2,
				voids
			});
		}
		if (!endVoid) {
			const point$2 = endRef.current;
			const [node$2] = getLeafNode(e$2, point$2);
			const { path: path$2 } = point$2;
			const offset = isSingleText ? start$1.offset : 0;
			const text = node$2.text.slice(offset, end$1.offset);
			e$2.apply({
				offset,
				path: path$2,
				text,
				type: "remove_text"
			});
		}
		if (!isSingleText && isAcrossBlocks && endRef.current && startRef.current) e$2.tf.mergeNodes({
			at: endRef.current,
			hanging: true,
			voids
		});
		const point$1 = endRef.unref() || startRef.unref();
		if (options.at == null && point$1) select$1(e$2, point$1);
	});
};

//#endregion
//#region src/utils/queryEditor.ts
/** Query the editor state. */
const queryEditor = (editor, { allow, at = editor.selection || [], exclude, filter, selectionAtBlockEnd, selectionAtBlockStart } = {}) => {
	if (filter && !filter(editor) || selectionAtBlockStart && !editor.api.isAt({ start: true }) || selectionAtBlockEnd && !editor.api.isAt({ end: true })) return false;
	const allows = castArray(allow);
	if (allows.length > 0 && !editor.api.some({
		at,
		match: { type: allows }
	})) return false;
	const excludes = castArray(exclude);
	if (excludes.length > 0 && editor.api.some({
		at,
		match: { type: excludes }
	})) return false;
	return true;
};

//#endregion
//#region src/utils/queryNode.ts
function castArray$1(value) {
	return Array.isArray(value) ? value : [value];
}
/** Query the node entry. */
const queryNode = (entry, { allow, exclude, filter, level, maxLevel } = {}) => {
	if (!entry) return false;
	const [node$2, path$2] = entry;
	if (level) {
		if (!castArray$1(level).includes(path$2.length)) return false;
	}
	if (maxLevel && path$2.length > maxLevel) return false;
	if (filter && !filter(entry)) return false;
	if (allow) {
		const allows = castArray$1(allow);
		if (allows.length > 0 && !allows.includes(node$2.type)) return false;
	}
	if (exclude) {
		const excludes = castArray$1(exclude);
		if (excludes.length > 0 && excludes.includes(node$2.type)) return false;
	}
	return true;
};

//#endregion
//#region src/internal/editor/editor-node.ts
const node$1 = (editor, atOrOptions, nodeOptions) => {
	try {
		if (LocationApi.isAt(atOrOptions)) return node(editor, getAt(editor, atOrOptions), nodeOptions);
		const options = atOrOptions;
		return editor.api.nodes(options).next().value;
	} catch {
		return;
	}
};

//#endregion
//#region src/internal/editor/editor-path.ts
const path$1 = (editor, at, options) => {
	try {
		return path(editor, getAt(editor, at), options);
	} catch {}
};

//#endregion
//#region src/internal/editor/getEdgePoints.ts
const getEdgePoints = (editor, at) => {
	try {
		return edges(editor, getAt(editor, at));
	} catch {}
};

//#endregion
//#region src/internal/editor/getEditorString.ts
const getEditorString = (editor, at = editor.selection, options) => {
	if (!at) return "";
	try {
		return string(editor, getAt(editor, at), options);
	} catch {
		return "";
	}
};

//#endregion
//#region src/internal/editor/getFirstNode.ts
const getFirstNode = (editor, at) => {
	try {
		return first(editor, getAt(editor, at));
	} catch {}
};

//#endregion
//#region src/internal/editor/getFragment.ts
const unwrapContainerNodes = (nodes$1, types) => {
	const unwrap = (nodes$2, acc = []) => {
		nodes$2.forEach((node$2) => {
			if (types?.includes(node$2.type)) unwrap(node$2.children, acc);
			else acc.push(node$2);
		});
		return acc;
	};
	return unwrap(nodes$1);
};
const getFragment$1 = (editor, at, options) => {
	if (at === null) return [];
	try {
		const result = at === void 0 ? getFragment(editor) : fragment(editor, getAt(editor, at));
		if (result.length > 0 && options?.unwrap && options.unwrap.length > 0) return unwrapContainerNodes(result, options.unwrap);
		return result;
	} catch {
		return [];
	}
};

//#endregion
//#region src/internal/editor/getLevels.ts
const getLevels = (editor, options) => levels(editor, getQueryOptions(editor, options));

//#endregion
//#region src/internal/editor/getMarks.ts
const getMarks = (editor) => marks(editor);

//#endregion
//#region src/internal/editor/getPathRefs.ts
const getPathRefs = (editor) => pathRefs(editor);

//#endregion
//#region src/internal/editor/getPoint.ts
const getPoint = (editor, at, options) => {
	try {
		return point(editor, getAt(editor, at), options);
	} catch {}
};

//#endregion
//#region src/internal/editor/getPointRefs.ts
const getPointRefs = (editor) => pointRefs(editor);

//#endregion
//#region src/internal/editor/getPositions.ts
const getPositions = (editor, options) => positions(editor, {
	...options,
	at: getAt(editor, options?.at)
});

//#endregion
//#region src/internal/editor/getRangeRefs.ts
const getRangeRefs = (editor) => rangeRefs(editor);

//#endregion
//#region src/internal/editor/hasBlocks.ts
const hasBlocks$1 = (editor, element) => hasBlocks(editor, element);

//#endregion
//#region src/internal/editor/hasInlines.ts
const hasInlines$1 = (editor, element) => hasInlines(editor, element);

//#endregion
//#region src/internal/editor/hasTexts.ts
const hasTexts$1 = (editor, element) => hasTexts(editor, element);

//#endregion
//#region src/internal/editor/insertBreak.ts
const insertBreak$1 = (editor) => insertBreak(editor);

//#endregion
//#region src/internal/editor/insertNode.ts
const insertNode = (editor, node$2, options) => editor.tf.insertNodes(node$2, options);

//#endregion
//#region src/internal/editor/isEdgePoint.ts
const isEdgePoint = (editor, point$1, at) => isEdge(editor, point$1, at);

//#endregion
//#region src/internal/editor/isEditorNormalizing.ts
const isEditorNormalizing = (editor) => isNormalizing(editor);

//#endregion
//#region src/internal/editor/isElementReadOnly.ts
const isElementReadOnly = (editor, options) => elementReadOnly(editor, options);

//#endregion
//#region src/internal/editor/isEmpty.ts
const isEmpty$1 = (editor, target = [], options) => {
	let _target = target;
	if (_target === null) return true;
	if (PathApi.isPath(_target) && _target.length === 0 || NodeApi.isEditor(_target)) return editor.children.length === 1 && isEmpty(editor, editor.children[0]);
	if (options?.after) {
		const blockAbove = editor.api.block({
			above: true,
			at: _target
		});
		if (!blockAbove) return false;
		const point$1 = editor.api.point(_target);
		const selectionParentEntry = editor.api.parent(_target);
		if (!selectionParentEntry) return false;
		const [, selectionParentPath] = selectionParentEntry;
		if (!editor.api.isEnd(point$1, selectionParentPath)) return false;
		const siblingNodes = Array.from(NodeApi.children(editor, blockAbove[1], { from: PathApi.lastIndex(point$1.path) + 1 })).map(([node$2]) => node$2);
		if (siblingNodes.length > 0) {
			for (const siblingNode of siblingNodes) if (TextApi.isText(siblingNode) && siblingNode.text) return false;
		} else return editor.api.isEnd(point$1, blockAbove[1]);
		return true;
	}
	if (PathApi.isPath(_target)) return isEmpty(editor, editor.api.node(_target)?.[0]);
	if (options?.block) {
		const block$1 = editor.api.block({ at: _target });
		if (!block$1) return false;
		_target = block$1[0];
	}
	if (!NodeApi.isNode(_target)) {
		const nodes$1 = editor.api.nodes({
			at: _target,
			...options
		});
		for (const node$2 of nodes$1) if (!isEmpty(editor, node$2[0])) return false;
		return true;
	}
	return isEmpty(editor, _target);
};

//#endregion
//#region src/internal/editor/isEndPoint.ts
const isEndPoint = (editor, point$1, at) => !!point$1 && isEnd(editor, point$1, at);

//#endregion
//#region src/internal/editor/isStartPoint.ts
const isStartPoint = (editor, point$1, at) => !!point$1 && isStart(editor, point$1, at);

//#endregion
//#region src/internal/editor/last.ts
const getNodeAtLevel = (editor, [node$2, path$2], level) => {
	const levelPath = path$2.slice(0, level + 1);
	const entry = editor.api.node(levelPath);
	if (!entry) return [node$2, path$2];
	return entry;
};
const last$1 = (editor, at, options = {}) => {
	try {
		const { level } = options;
		const lastNodeEntry = last(editor, getAt(editor, at));
		if (lastNodeEntry && typeof level === "number") {
			if (editor.children.length === 0) return;
			return getNodeAtLevel(editor, lastNodeEntry, level);
		}
		return lastNodeEntry;
	} catch {}
};

//#endregion
//#region src/internal/editor/next.ts
const next = (editor, options = {}) => {
	const { from = "after", mode = from === "child" ? "all" : "lowest", voids = false } = options;
	let match$1 = getMatch(editor, options);
	const at = getAt(editor, options.at) ?? editor.selection;
	if (!at) return;
	let start$1;
	if (from === "child" && PathApi.isPath(at)) {
		const path$2 = PathApi.firstChild(at);
		if (editor.api.node(path$2)) {
			start$1 = path$2;
			match$1 = combineMatch((_n, p) => !PathApi.isAncestor(p, at) && !PathApi.equals(p, at), match$1);
		}
	}
	if (!start$1) {
		const pointAfterLocation = editor.api.after(at, { voids });
		if (!pointAfterLocation) return;
		start$1 = pointAfterLocation.path;
	}
	const [, to] = editor.api.last([]);
	const span = [start$1, to];
	if (PathApi.isPath(at) && at.length === 0) return;
	if (match$1 == null) if (PathApi.isPath(at)) {
		const [parent$2] = editor.api.parent(at);
		match$1 = (n$1) => parent$2.children.includes(n$1);
	} else match$1 = () => true;
	const [next$1] = editor.api.nodes({
		at: span,
		match: match$1,
		mode,
		voids
	});
	return next$1;
};

//#endregion
//#region src/internal/editor/normalizeEditor.ts
const normalizeEditor = (editor, options) => normalize(editor, options);

//#endregion
//#region src/internal/editor/normalizeNode.ts
const normalizeNode$1 = (editor, entry, options) => {
	const value = editor.meta.isNormalizing;
	editor.meta.isNormalizing = true;
	normalizeNode(editor, entry, options);
	editor.meta.isNormalizing = value;
};

//#endregion
//#region src/internal/editor/parent.ts
const parent$1 = (editor, at, options) => {
	try {
		return parent(editor, getAt(editor, at), options);
	} catch {}
};

//#endregion
//#region src/internal/editor/previous.ts
const previousBase = (editor, options) => {
	const { from = "after", mode = "lowest", voids = false } = options;
	let match$1 = getMatch(editor, options);
	const at = getAt(editor, options.at) ?? editor.selection;
	if (!at) return;
	let start$1;
	if (from === "parent" && PathApi.isPath(at) && at.length > 1) {
		start$1 = at;
		match$1 = combineMatch((_n, p) => {
			return !PathApi.isAfter(p, at) && !PathApi.equals(p, at);
		}, match$1);
	}
	if (!start$1) {
		const pointBeforeLocation = editor.api.before(at, { voids });
		if (!pointBeforeLocation) return;
		start$1 = pointBeforeLocation.path;
	}
	const [, to] = editor.api.first([]);
	const span = [start$1, to];
	if (PathApi.isPath(at) && at.length === 0) return;
	if (match$1 == null) if (PathApi.isPath(at)) {
		const [parent$2] = editor.api.parent(at);
		match$1 = (n$1) => parent$2.children.includes(n$1);
	} else match$1 = () => true;
	const [previous$1] = editor.api.nodes({
		at: span,
		match: match$1,
		mode,
		reverse: true,
		voids
	});
	return previous$1;
};
const previous = (editor, options) => {
	const getPrevious = (o$2) => {
		try {
			return previousBase(editor, o$2);
		} catch {}
	};
	if (options?.sibling) {
		const path$2 = getQueryOptions(editor, options).at;
		if (!path$2) return;
		const previousPath = PathApi.previous(path$2);
		if (!previousPath) return;
		return editor.api.node(previousPath);
	}
	if (!(options?.id && options?.block)) return getPrevious(options);
	const block$1 = editor.api.node({
		id: options.id,
		at: []
	});
	if (!block$1) return;
	return getPrevious({
		at: block$1[1],
		block: true
	});
};

//#endregion
//#region src/internal/editor/range.ts
const range$1 = (editor, at, to, options) => {
	let from = getAt(editor, at);
	if (RangeApi.isRange(from) && !to) return from;
	if (from === "start") {
		const path$2 = editor.api.block({ at: to })?.[1];
		if (!path$2) return;
		const anchor = editor.api.start(path$2);
		if (!anchor) return;
		const focus$1 = PointApi.get(to);
		if (!focus$1) return;
		return {
			anchor,
			focus: focus$1
		};
	}
	if (to && from === "before") from = editor.api.before(to, options?.before) ?? getAt(editor, to);
	return range(editor, from, getAt(editor, to));
};

//#endregion
//#region src/internal/editor/removeEditorMark.ts
const removeEditorMark = (editor, key) => removeMark(editor, key);

//#endregion
//#region src/internal/editor/shouldMergeNodes.ts
const shouldMergeNodes = (editor, prevNodeEntry, _) => {
	const [prevNode, prevPath] = prevNodeEntry;
	if (ElementApi.isElement(prevNode) && editor.api.isEmpty(prevNode) || TextApi.isText(prevNode) && prevNode.text === "" && prevPath.at(-1) !== 0) {
		editor.tf.removeNodes({ at: prevPath });
		return false;
	}
	return true;
};

//#endregion
//#region src/internal/editor/unhangRange.ts
const unhangRange$1 = (editor, range$2, options = {}) => {
	const { character, unhang = true, voids } = options;
	if (!RangeApi.isRange(range$2)) return range$2;
	if (character) {
		let [start$1, end$1] = RangeApi.edges(range$2);
		if (!PathApi.equals(start$1.path, end$1.path)) if (end$1.offset === 0) {
			const pointAfter = editor.api.after(start$1);
			if (pointAfter) end$1 = pointAfter;
		} else {
			const pointBefore = editor.api.before(end$1);
			if (pointBefore) start$1 = pointBefore;
		}
		return {
			anchor: start$1,
			focus: end$1
		};
	}
	if (unhang) return unhangRange(editor, range$2, { voids });
	return range$2;
};

//#endregion
//#region src/internal/editor-extension/edge-blocks.ts
const edgeBlocks = (editor, { at: _at, ...options } = {}) => {
	const at = _at ?? editor.selection;
	if (!at) return null;
	const [start$1, end$1] = editor.api.edges(at ?? editor.selection);
	const startBlock = editor.api.block({
		at: start$1,
		...options
	});
	if (!startBlock) return null;
	const endBlock = editor.api.block({
		at: end$1,
		...options
	});
	if (!endBlock) return null;
	return [startBlock, endBlock];
};

//#endregion
//#region src/internal/editor-extension/editor-block.ts
const block = (editor, { above: above$2, highest, ...options } = {}) => {
	if (highest) {
		const target = options.at ?? editor.selection;
		if (!target) return;
		const index = editor.api.path(target)?.[0];
		if (index === void 0) return;
		return editor.api.node([index]);
	}
	if (above$2) return editor.api.above({
		...options,
		block: true
	});
	return editor.api.node({
		...options,
		block: true,
		mode: "lowest"
	});
};

//#endregion
//#region src/internal/editor-extension/editor-blocks.ts
const blocks = (editor, options) => [...editor.api.nodes({
	...options,
	block: true
})];

//#endregion
//#region src/internal/editor-extension/editor-descendant.ts
/**
* Iterate through all of the nodes in the editor and return the first match. If
* no match is found, return undefined.
*/
const descendant = (editor, options) => {
	try {
		const { at = editor.selection, match: _match, reverse = false, voids = false } = options;
		if (!at) return;
		let from;
		let to;
		if (SpanApi.isSpan(at)) [from, to] = at;
		else if (RangeApi.isRange(at)) {
			const first$1 = editor.api.path(at, { edge: "start" });
			const last$2 = editor.api.path(at, { edge: "end" });
			from = reverse ? last$2 : first$1;
			to = reverse ? first$1 : last$2;
		}
		let root = [editor, []];
		if (PathApi.isPath(at)) root = editor.api.node(at);
		const nodeEntries = NodeApi.descendants(root[0], {
			from,
			reverse,
			to,
			pass: ([n$1]) => voids ? false : editor.api.isVoid(n$1)
		});
		for (const [node$2, path$2] of nodeEntries) if (match(node$2, path$2, _match)) return [node$2, at.concat(path$2)];
	} catch {
		return;
	}
};

//#endregion
//#region src/internal/editor-extension/editor-mark.ts
const mark = (editor, key) => {
	return editor.api.marks()?.[key];
};

//#endregion
//#region src/internal/editor-extension/hasMark.ts
const hasMark = (editor, key) => !!editor.api.mark(key);

//#endregion
//#region src/internal/editor-extension/is-selected.ts
const isSelected = (editor, target, options = {}) => {
	const { contains = false } = options;
	if (!editor.selection) return false;
	const range$2 = RangeApi.isRange(target) ? target : editor.api.range(target);
	if (!range$2) return false;
	if (contains) return RangeApi.contains(editor.selection, range$2);
	return !!RangeApi.intersection(editor.selection, range$2);
};

//#endregion
//#region src/internal/editor-extension/isAt.ts
const WHITESPACE_OR_END_REGEX = /^(?:\s|$)/;
/**
* Check if a location (point/range) is at a specific position.
*
* For ranges:
*
* - If text=true, check if range is in single text node
* - If block=true, check if range is in single block
* - If blocks=true, check if range is across multiple blocks
* - If start=true, check if range starts at block start
* - If end=true, check if range ends at block end
*
* For points:
*
* - If word=true, check relative to word boundaries
* - If start=true, check if at start
* - If end=true, check if at end
*/
const isAt = (editor, { at = editor.selection, block: block$1, blocks: blocks$1, end: end$1, start: start$1, text, word, ...options } = {}) => {
	if (!at) return false;
	if (PointApi.isPoint(at)) {
		if (word && end$1) {
			const after$1 = editor.api.after(at);
			if (!after$1) return true;
			const afterRange = editor.api.range(at, after$1);
			const afterText = editor.api.string(afterRange);
			return WHITESPACE_OR_END_REGEX.test(afterText);
		}
		return false;
	}
	if (RangeApi.isRange(at)) {
		const [startPoint, endPoint] = RangeApi.edges(at);
		if (text) return PathApi.equals(startPoint.path, endPoint.path);
		const startBlock = editor.api.block({
			at: startPoint,
			...options
		});
		const endBlock = editor.api.block({
			at: endPoint,
			...options
		});
		if (blocks$1) {
			if (!startBlock && !endBlock) return false;
			if (!startBlock || !endBlock) return true;
			return !PathApi.equals(startBlock[1], endBlock[1]);
		}
		if (!startBlock || !endBlock) return false;
		if (block$1) return PathApi.equals(startBlock[1], endBlock[1]);
		if (start$1) return editor.api.isStart(startPoint, startBlock[1]) || RangeApi.isExpanded(at) && editor.api.isStart(endPoint, startBlock[1]);
		if (end$1) return editor.api.isEnd(endPoint, endBlock[1]);
	}
	return false;
};

//#endregion
//#region src/internal/editor-extension/isEditorEnd.ts
const isEditorEnd = (editor) => {
	if (editor.selection) {
		const point$1 = editor.selection.focus;
		const endPoint = editor.api.end([]);
		return endPoint.offset === 0 && editor.api.isEnd(point$1, point$1) && PathApi.equals(PathApi.next(PathApi.parent(point$1.path)), endPoint.path);
	}
	return false;
};

//#endregion
//#region src/internal/editor-extension/isText.ts
/** Check if a node at a location is a Text node */
const isText = (editor, at) => {
	const node$2 = editor.api.node(at)?.[0];
	return TextApi.isText(node$2);
};

//#endregion
//#region src/internal/editor-extension/nodes-range.ts
const nodesRange = (editor, nodes$1) => {
	if (nodes$1.length === 0) return;
	const firstBlockPath = nodes$1[0][1];
	const lastBlockPath = nodes$1.at(-1)[1];
	return editor.api.range(firstBlockPath, lastBlockPath);
};

//#endregion
//#region src/internal/editor-extension/prop.ts
function prop({ key, defaultValue, getProp, mode = "block", nodes: nodes$1 }) {
	if (nodes$1.length === 0) return defaultValue;
	const getNodeValue = getProp ?? ((node$2) => node$2[key]);
	let value;
	for (const node$2 of nodes$1) {
		if (mode === "block" || mode === "all") {
			const nodeValue = getNodeValue(node$2);
			if (nodeValue !== void 0) {
				if (value === void 0) value = nodeValue;
				else if (value !== nodeValue) return;
				if (mode === "block") continue;
			} else if (mode === "block") return defaultValue;
		}
		if (mode === "text" || mode === "all") {
			const textEntries = Array.from(NodeApi.texts(node$2));
			for (const [text] of textEntries) {
				const textValue = getNodeValue(text);
				if (textValue !== void 0) {
					if (value === void 0) value = textValue;
					else if (value !== textValue) return;
				} else if (mode === "text") return defaultValue;
			}
		}
	}
	return value;
}

//#endregion
//#region ../../node_modules/compute-scroll-into-view/dist/index.js
const t = (t$1) => "object" == typeof t$1 && null != t$1 && 1 === t$1.nodeType, e$1 = (t$1, e$2) => (!e$2 || "hidden" !== t$1) && "visible" !== t$1 && "clip" !== t$1, n = (t$1, n$1) => {
	if (t$1.clientHeight < t$1.scrollHeight || t$1.clientWidth < t$1.scrollWidth) {
		const o$2 = getComputedStyle(t$1, null);
		return e$1(o$2.overflowY, n$1) || e$1(o$2.overflowX, n$1) || ((t$2) => {
			const e$2 = ((t$3) => {
				if (!t$3.ownerDocument || !t$3.ownerDocument.defaultView) return null;
				try {
					return t$3.ownerDocument.defaultView.frameElement;
				} catch (t$4) {
					return null;
				}
			})(t$2);
			return !!e$2 && (e$2.clientHeight < t$2.scrollHeight || e$2.clientWidth < t$2.scrollWidth);
		})(t$1);
	}
	return !1;
}, o$1 = (t$1, e$2, n$1, o$2, l$1, r$1, i, s) => r$1 < t$1 && i > e$2 || r$1 > t$1 && i < e$2 ? 0 : r$1 <= t$1 && s <= n$1 || i >= e$2 && s >= n$1 ? r$1 - t$1 - o$2 : i > e$2 && s < n$1 || r$1 < t$1 && s > n$1 ? i - e$2 + l$1 : 0, l = (t$1) => {
	const e$2 = t$1.parentElement;
	return null == e$2 ? t$1.getRootNode().host || null : e$2;
}, r = (e$2, r$1) => {
	var i, s, d, h;
	if ("undefined" == typeof document) return [];
	const { scrollMode: c, block: f, inline: u, boundary: a, skipOverflowHiddenElements: g } = r$1, p = "function" == typeof a ? a : (t$1) => t$1 !== a;
	if (!t(e$2)) throw new TypeError("Invalid target");
	const m = document.scrollingElement || document.documentElement, w = [];
	let W = e$2;
	for (; t(W) && p(W);) {
		if (W = l(W), W === m) {
			w.push(W);
			break;
		}
		null != W && W === document.body && n(W) && !n(document.documentElement) || null != W && n(W, g) && w.push(W);
	}
	const b = null != (s = null == (i = window.visualViewport) ? void 0 : i.width) ? s : innerWidth, H = null != (h = null == (d = window.visualViewport) ? void 0 : d.height) ? h : innerHeight, { scrollX: y, scrollY: M } = window, { height: v, width: E, top: x, right: C, bottom: I, left: R } = e$2.getBoundingClientRect(), { top: T, right: B, bottom: F, left: V } = ((t$1) => {
		const e$3 = window.getComputedStyle(t$1);
		return {
			top: parseFloat(e$3.scrollMarginTop) || 0,
			right: parseFloat(e$3.scrollMarginRight) || 0,
			bottom: parseFloat(e$3.scrollMarginBottom) || 0,
			left: parseFloat(e$3.scrollMarginLeft) || 0
		};
	})(e$2);
	let k = "start" === f || "nearest" === f ? x - T : "end" === f ? I + F : x + v / 2 - T + F, D = "center" === u ? R + E / 2 - V + B : "end" === u ? C + B : R - V;
	const L = [];
	for (let t$1 = 0; t$1 < w.length; t$1++) {
		const e$3 = w[t$1], { height: l$1, width: r$2, top: i$1, right: s$1, bottom: d$1, left: h$1 } = e$3.getBoundingClientRect();
		if ("if-needed" === c && x >= 0 && R >= 0 && I <= H && C <= b && (e$3 === m && !n(e$3) || x >= i$1 && I <= d$1 && R >= h$1 && C <= s$1)) return L;
		const a$1 = getComputedStyle(e$3), g$1 = parseInt(a$1.borderLeftWidth, 10), p$1 = parseInt(a$1.borderTopWidth, 10), W$1 = parseInt(a$1.borderRightWidth, 10), T$1 = parseInt(a$1.borderBottomWidth, 10);
		let B$1 = 0, F$1 = 0;
		const V$1 = "offsetWidth" in e$3 ? e$3.offsetWidth - e$3.clientWidth - g$1 - W$1 : 0, S = "offsetHeight" in e$3 ? e$3.offsetHeight - e$3.clientHeight - p$1 - T$1 : 0, X = "offsetWidth" in e$3 ? 0 === e$3.offsetWidth ? 0 : r$2 / e$3.offsetWidth : 0, Y = "offsetHeight" in e$3 ? 0 === e$3.offsetHeight ? 0 : l$1 / e$3.offsetHeight : 0;
		if (m === e$3) B$1 = "start" === f ? k : "end" === f ? k - H : "nearest" === f ? o$1(M, M + H, H, p$1, T$1, M + k, M + k + v, v) : k - H / 2, F$1 = "start" === u ? D : "center" === u ? D - b / 2 : "end" === u ? D - b : o$1(y, y + b, b, g$1, W$1, y + D, y + D + E, E), B$1 = Math.max(0, B$1 + M), F$1 = Math.max(0, F$1 + y);
		else {
			B$1 = "start" === f ? k - i$1 - p$1 : "end" === f ? k - d$1 + T$1 + S : "nearest" === f ? o$1(i$1, d$1, l$1, p$1, T$1 + S, k, k + v, v) : k - (i$1 + l$1 / 2) + S / 2, F$1 = "start" === u ? D - h$1 - g$1 : "center" === u ? D - (h$1 + r$2 / 2) + V$1 / 2 : "end" === u ? D - s$1 + W$1 + V$1 : o$1(h$1, s$1, r$2, g$1, W$1 + V$1, D, D + E, E);
			const { scrollLeft: t$2, scrollTop: n$1 } = e$3;
			B$1 = 0 === Y ? 0 : Math.max(0, Math.min(n$1 + B$1 / Y, e$3.scrollHeight - l$1 / Y + S)), F$1 = 0 === X ? 0 : Math.max(0, Math.min(t$2 + F$1 / X, e$3.scrollWidth - r$2 / X + V$1)), k += n$1 - B$1, D += t$2 - F$1;
		}
		L.push({
			el: e$3,
			top: B$1,
			left: F$1
		});
	}
	return L;
};

//#endregion
//#region ../../node_modules/scroll-into-view-if-needed/dist/index.js
const o = (t$1) => !1 === t$1 ? {
	block: "end",
	inline: "nearest"
} : ((t$2) => t$2 === Object(t$2) && 0 !== Object.keys(t$2).length)(t$1) ? t$1 : {
	block: "start",
	inline: "nearest"
};
function e(e$2, r$1) {
	if (!e$2.isConnected || !((t$1) => {
		let o$2 = t$1;
		for (; o$2 && o$2.parentNode;) {
			if (o$2.parentNode === document) return !0;
			o$2 = o$2.parentNode instanceof ShadowRoot ? o$2.parentNode.host : o$2.parentNode;
		}
		return !1;
	})(e$2)) return;
	const n$1 = ((t$1) => {
		const o$2 = window.getComputedStyle(t$1);
		return {
			top: parseFloat(o$2.scrollMarginTop) || 0,
			right: parseFloat(o$2.scrollMarginRight) || 0,
			bottom: parseFloat(o$2.scrollMarginBottom) || 0,
			left: parseFloat(o$2.scrollMarginLeft) || 0
		};
	})(e$2);
	if (((t$1) => "object" == typeof t$1 && "function" == typeof t$1.behavior)(r$1)) return r$1.behavior(r(e$2, r$1));
	const l$1 = "boolean" == typeof r$1 || null == r$1 ? void 0 : r$1.behavior;
	for (const { el: a, top: i, left: s } of r(e$2, o(r$1))) {
		const t$1 = i - n$1.top + n$1.bottom, o$2 = s - n$1.left + n$1.right;
		a.scroll({
			top: t$1,
			left: o$2,
			behavior: l$1
		});
	}
}

//#endregion
//#region src/internal/editor-extension/scrollIntoView.ts
const defaultOptions = { scrollMode: "if-needed" };
function scrollIntoView(editor, target, options = defaultOptions) {
	requestAnimationFrame(() => {
		let domRange;
		if (PointApi.isPoint(target)) {
			const { offset = 0, path: path$2 } = target;
			domRange = editor.api.toDOMRange({
				anchor: {
					offset,
					path: path$2
				},
				focus: {
					offset,
					path: path$2
				}
			});
		} else domRange = target;
		if (!domRange) return;
		const leafEl = domRange.startContainer.parentElement;
		leafEl.getBoundingClientRect = domRange.getBoundingClientRect.bind(domRange);
		e(leafEl, options);
		setTimeout(() => {
			leafEl.getBoundingClientRect = void 0;
		}, 0);
	});
}

//#endregion
//#region src/internal/editor-extension/some.ts
/**
* Iterate through all of the nodes in the editor and break early for the first
* truthy match. Otherwise returns false.
*/
const some = (editor, options) => !!editor.api.node(options);

//#endregion
//#region src/internal/transforms/collapseSelection.ts
const collapseSelection = (editor, options) => {
	collapse(editor, options);
};

//#endregion
//#region src/internal/transforms/deleteText.ts
const THAI_SCRIPT_REGEX = /[\u0E00-\u0E7F]+/;
const deleteText = (editor, options = {}) => {
	let at = getAt(editor, options?.at) ?? editor.selection;
	editor.tf.withoutNormalizing(() => {
		const { distance = 1, reverse = false, unit = "character", voids = false } = options;
		let { hanging = false } = options;
		if (!at) return;
		let isCollapsed = false;
		if (RangeApi.isRange(at) && RangeApi.isCollapsed(at)) {
			isCollapsed = true;
			at = at.anchor;
		}
		if (PointApi.isPoint(at)) {
			const furthestVoid = editor.api.void({
				at,
				mode: "highest"
			});
			if (!voids && furthestVoid) {
				const [, voidPath] = furthestVoid;
				at = voidPath;
			} else {
				const opts = {
					distance,
					unit
				};
				const target = reverse ? editor.api.before(at, opts) || editor.api.start([]) : editor.api.after(at, opts) || editor.api.end([]);
				at = {
					anchor: at,
					focus: target
				};
				hanging = true;
			}
		}
		if (PathApi.isPath(at)) {
			editor.tf.removeNodes({
				at,
				voids
			});
			return;
		}
		if (RangeApi.isCollapsed(at)) return;
		if (!hanging) {
			const [, end$2] = RangeApi.edges(at);
			const endOfDoc = editor.api.end([]);
			if (!PointApi.equals(end$2, endOfDoc)) at = editor.api.unhangRange(at, { voids });
		}
		let [start$1, end$1] = RangeApi.edges(at);
		const startBlock = editor.api.above({
			at: start$1,
			voids,
			match: (n$1) => ElementApi.isElement(n$1) && editor.api.isBlock(n$1)
		});
		const endBlock = editor.api.above({
			at: end$1,
			voids,
			match: (n$1) => ElementApi.isElement(n$1) && editor.api.isBlock(n$1)
		});
		const isAcrossBlocks = startBlock && endBlock && !PathApi.equals(startBlock[1], endBlock[1]);
		const isSingleText = PathApi.equals(start$1.path, end$1.path);
		const startNonEditable = voids ? null : editor.api.void({
			at: start$1,
			mode: "highest"
		}) ?? editor.api.elementReadOnly({
			at: start$1,
			mode: "highest"
		});
		const endNonEditable = voids ? null : editor.api.void({
			at: end$1,
			mode: "highest"
		}) ?? editor.api.elementReadOnly({
			at: end$1,
			mode: "highest"
		});
		if (startNonEditable) {
			const before$1 = editor.api.before(start$1);
			if (before$1 && startBlock && PathApi.isAncestor(startBlock[1], before$1.path)) start$1 = before$1;
		}
		if (endNonEditable) {
			const after$1 = editor.api.after(end$1);
			if (after$1 && endBlock && PathApi.isAncestor(endBlock[1], after$1.path)) end$1 = after$1;
		}
		const matches = [];
		let lastPath;
		for (const entry of editor.api.nodes({
			at,
			voids
		})) {
			const [node$2, path$2] = entry;
			if (lastPath && PathApi.compare(path$2, lastPath) === 0) continue;
			if (!voids && ElementApi.isElement(node$2) && editor.api.isElementReadOnly(node$2) || !PathApi.isCommon(path$2, start$1.path) && !PathApi.isCommon(path$2, end$1.path)) {
				matches.push(entry);
				lastPath = path$2;
			}
		}
		const pathRefs$1 = Array.from(matches, ([, p]) => editor.api.pathRef(p));
		const startRef = editor.api.pointRef(start$1);
		const endRef = editor.api.pointRef(end$1);
		let removedText = "";
		if (!isSingleText && !startNonEditable) {
			const point$2 = startRef.current;
			const [node$2] = editor.api.leaf(point$2);
			const { path: path$2 } = point$2;
			const { offset } = start$1;
			const text = node$2.text.slice(offset);
			if (text.length > 0) {
				editor.tf.apply({
					offset,
					path: path$2,
					text,
					type: "remove_text"
				});
				removedText = text;
			}
		}
		const paths = pathRefs$1.reverse().map((r$1) => r$1.unref()).filter((r$1) => r$1 !== null);
		for (const p of paths) editor.tf.removeNodes({
			at: p,
			voids
		});
		if (!endNonEditable) {
			const point$2 = endRef.current;
			const [node$2] = editor.api.leaf(point$2);
			const { path: path$2 } = point$2;
			const offset = isSingleText ? start$1.offset : 0;
			const text = node$2.text.slice(offset, end$1.offset);
			if (text.length > 0) {
				editor.tf.apply({
					offset,
					path: path$2,
					text,
					type: "remove_text"
				});
				removedText = text;
			}
		}
		if (!isSingleText && isAcrossBlocks && endRef.current && startRef.current) editor.tf.mergeNodes({
			at: endRef.current,
			hanging: true,
			reverse: !reverse,
			voids
		});
		if (isCollapsed && reverse && unit === "character" && removedText.length > 1 && THAI_SCRIPT_REGEX.exec(removedText)) editor.tf.insertText(removedText.slice(0, removedText.length - distance));
		const startUnref = startRef.unref();
		const endUnref = endRef.unref();
		const point$1 = reverse ? startUnref || endUnref : endUnref || startUnref;
		if (options?.at == null && point$1) editor.tf.select(point$1);
	});
};

//#endregion
//#region src/internal/transforms/deselect.ts
const deselect$1 = (editor) => {
	deselect(editor);
};

//#endregion
//#region src/internal/transforms/insertFragment.ts
const insertFragment$1 = (editor, fragment$1, options) => {
	insertFragment(editor, fragment$1, {
		...options,
		at: getAt(editor, options?.at)
	});
};

//#endregion
//#region src/internal/transforms/insertNodes.ts
const insertNodes$1 = (editor, nodes$1, { nextBlock, removeEmpty, ...options } = {}) => {
	options = getQueryOptions(editor, options);
	editor.tf.withoutNormalizing(() => {
		if (removeEmpty) {
			const blockEntry = editor.api.above({ at: options.at });
			if (blockEntry) {
				const queryNodeOptions = removeEmpty === true ? { allow: ["p"] } : removeEmpty;
				const { filter } = queryNodeOptions;
				queryNodeOptions.filter = ([node$2, path$2]) => {
					if (NodeApi.string(node$2)) return false;
					if (node$2.children.some((n$1) => editor.api.isInline(n$1))) return false;
					return !filter || filter([node$2, path$2]);
				};
				if (queryNode(blockEntry, queryNodeOptions)) {
					editor.tf.removeNodes({ at: blockEntry[1] });
					nextBlock = false;
				}
			}
		}
		if (nextBlock) {
			const { at = editor.selection } = options;
			if (at) {
				const endPoint = editor.api.end(at);
				const blockEntry = editor.api.above({
					at: endPoint,
					block: true
				});
				if (blockEntry) options.at = PathApi.next(blockEntry[1]);
			}
		}
		insertNodes(editor, nodes$1, options);
	});
};

//#endregion
//#region src/internal/transforms/insertSoftBreak.ts
/**
* Insert a soft break at the current selection. If the selection is currently
* expanded, delete it first.
*/
const insertSoftBreak = (editor) => {
	editor.tf.withoutNormalizing(() => {
		if (editor.api.isExpanded()) editor.tf.delete();
		editor.tf.insertText("\n");
	});
};

//#endregion
//#region src/internal/transforms/insertText.ts
const insertText = (editor, text, { marks: marks$1 = true, ...options } = {}) => {
	const at = getAt(editor, options.at);
	if (at) {
		Transforms.insertText(editor, text, {
			...options,
			at
		});
		return;
	}
	if (editor.selection) if (marks$1 && editor.marks) {
		const node$2 = {
			text,
			...editor.marks
		};
		editor.tf.insertNodes(node$2, { voids: options.voids });
		editor.marks = null;
	} else Transforms.insertText(editor, text, options);
};

//#endregion
//#region src/internal/transforms/liftNodes.ts
const liftNodes$1 = (editor, options) => liftNodes(editor, getQueryOptions(editor, options));

//#endregion
//#region src/internal/transforms/mergeNodes.ts
const hasSingleChildNest = (editor, node$2) => {
	if (ElementApi.isElement(node$2)) {
		const element = node$2;
		if (editor.api.isVoid(node$2)) return true;
		if (element.children.length === 1) return hasSingleChildNest(editor, element.children[0]);
		return false;
	}
	if (NodeApi.isEditor(node$2)) return false;
	return true;
};
const mergeNodes = (editor, options = {}) => {
	const _options = getQueryOptions(editor, options);
	editor.tf.withoutNormalizing(() => {
		let { at = editor.selection, match: match$1 } = _options;
		const { hanging = false, mode = "lowest", voids = false } = _options;
		if (!at) return;
		if (match$1 == null) if (PathApi.isPath(at)) {
			const [parent$2] = editor.api.parent(at);
			match$1 = (n$1) => parent$2.children.includes(n$1);
		} else match$1 = (n$1) => ElementApi.isElement(n$1) && editor.api.isBlock(n$1);
		if (!hanging && RangeApi.isRange(at)) at = editor.api.unhangRange(at);
		if (RangeApi.isRange(at)) if (RangeApi.isCollapsed(at)) at = at.anchor;
		else {
			const [, end$1] = RangeApi.edges(at);
			const pointRef$1 = editor.api.pointRef(end$1);
			editor.tf.delete({ at });
			at = pointRef$1.unref();
			if (_options.at == null) editor.tf.select(at);
		}
		const _nodes = editor.api.nodes({
			at,
			match: match$1,
			mode,
			voids
		});
		const [current] = Array.from(_nodes);
		const prev = editor.api.previous({
			at,
			match: match$1,
			mode,
			voids
		});
		if (!current || !prev) return;
		const [node$2, path$2] = current;
		const [prevNode, prevPath] = prev;
		if (path$2.length === 0 || prevPath.length === 0) return;
		const newPath = PathApi.next(prevPath);
		const commonPath = PathApi.common(path$2, prevPath);
		const isPreviousSibling = PathApi.isSibling(path$2, prevPath);
		const _levels = editor.api.levels({ at: path$2 });
		const levels$1 = new Set(Array.from(_levels, ([n$1]) => n$1).slice(commonPath.length).slice(0, -1));
		const emptyAncestor = editor.api.above({
			at: path$2,
			mode: "highest",
			match: (n$1) => levels$1.has(n$1) && hasSingleChildNest(editor, n$1)
		});
		const emptyRef = emptyAncestor && editor.api.pathRef(emptyAncestor[1]);
		let properties;
		let position;
		if (TextApi.isText(node$2) && TextApi.isText(prevNode)) {
			const { text, ...rest } = node$2;
			position = prevNode.text.length;
			properties = rest;
		} else if (ElementApi.isElement(node$2) && ElementApi.isElement(prevNode)) {
			const { children, ...rest } = node$2;
			position = prevNode.children.length;
			properties = rest;
		} else throw new TypeError(`Cannot merge the node at path [${path$2}] with the previous sibling because it is not the same kind: ${JSON.stringify(node$2)} ${JSON.stringify(prevNode)}`);
		if (!editor.api.shouldMergeNodes(prev, current, { reverse: _options.reverse })) return;
		if (!isPreviousSibling) editor.tf.moveNodes({
			at: path$2,
			to: newPath,
			voids
		});
		if (emptyRef) editor.tf.removeNodes({
			at: emptyRef.current,
			event: { type: "mergeNodes" },
			voids
		});
		if (emptyRef) emptyRef.unref();
		editor.tf.apply({
			path: newPath,
			position,
			properties,
			type: "merge_node"
		});
	});
};

//#endregion
//#region src/internal/transforms/moveNodes.ts
const moveNodes$1 = (editor, { children, fromIndex = 0, ...opt }) => {
	const options = getQueryOptions(editor, opt);
	let moved = false;
	if (children) {
		if (!options.at) return moved;
		const entry = editor.api.node(options.at);
		if (!entry) return moved;
		const [node$2, path$2] = entry;
		if (!editor.api.isBlock(node$2)) return moved;
		for (let i = node$2.children.length - 1; i >= fromIndex; i--) {
			const childPath = [...path$2, i];
			const childNode = NodeApi.get(editor, childPath);
			if (!options.match || childNode && options.match(childNode, childPath)) {
				moveNodes(editor, {
					...options,
					at: childPath
				});
				moved = true;
			}
		}
		return moved;
	}
	return moveNodes(editor, options);
};

//#endregion
//#region src/internal/transforms/moveSelection.ts
const moveSelection = (editor, options) => {
	move(editor, options);
};

//#endregion
//#region src/internal/transforms/removeNodes.ts
const removeNodes$1 = (editor, { children, previousEmptyBlock, ...opt } = {}) => {
	const options = getQueryOptions(editor, opt);
	editor.tf.withoutNormalizing(() => {
		if (previousEmptyBlock) {
			const entry = editor.api.block({ at: options.at });
			if (!entry) return;
			const prevEntry = editor.api.previous({
				at: entry[1],
				sibling: true
			});
			if (!prevEntry) return;
			const [prevNode, prevPath] = prevEntry;
			if (editor.api.isEmpty(prevNode)) editor.tf.removeNodes({ at: prevPath });
			return;
		}
		if (children && options.at) {
			for (const [, childPath] of NodeApi.children(editor, options.at, { reverse: true })) editor.tf.removeNodes({
				...options,
				at: childPath
			});
			return;
		}
		return removeNodes(editor, getQueryOptions(editor, options));
	});
};

//#endregion
//#region src/internal/transforms/setNodes.ts
const setNodes$1 = (editor, props, { marks: marks$1, ...options } = {}) => {
	if (marks$1) {
		let at = getAt(editor, options.at) ?? editor.selection;
		if (!at) return;
		if (PathApi.isPath(at)) at = editor.api.range(at);
		if (!RangeApi.isRange(at)) return;
		const match$1 = (node$2, path$2) => {
			if (!TextApi.isText(node$2)) return false;
			const parentEntry = editor.api.parent(path$2);
			if (!parentEntry) return false;
			const [parentNode] = parentEntry;
			return !editor.api.isVoid(parentNode) || editor.api.markableVoid(parentNode);
		};
		const isExpandedRange = RangeApi.isExpanded(at);
		let markAcceptingVoidSelected = false;
		if (!isExpandedRange) {
			const selectedEntry = editor.api.node(at);
			if (!selectedEntry) return;
			const [selectedNode, selectedPath] = selectedEntry;
			if (selectedNode && match$1(selectedNode, selectedPath)) {
				const parentEntry = editor.api.parent(selectedPath);
				if (!parentEntry) return;
				const [parentNode] = parentEntry;
				markAcceptingVoidSelected = parentNode && editor.api.markableVoid(parentNode);
			}
		}
		if (isExpandedRange || markAcceptingVoidSelected) return setNodes(editor, props, getQueryOptions(editor, {
			...options,
			at,
			match: match$1,
			split: true,
			voids: true
		}));
	}
	return setNodes(editor, props, getQueryOptions(editor, options));
};

//#endregion
//#region src/internal/transforms/setPoint.ts
const setPoint$1 = (editor, props, options) => {
	setPoint(editor, props, options);
};

//#endregion
//#region src/internal/transforms/setSelection.ts
const setSelection$1 = (editor, props) => {
	setSelection(editor, props);
};

//#endregion
//#region src/internal/transforms/splitNodes.ts
const splitNodes$1 = (editor, options) => splitNodes(editor, getQueryOptions(editor, options));

//#endregion
//#region src/internal/transforms/unsetNodes.ts
const unsetNodes$1 = (editor, props, options) => unsetNodes(editor, props, getQueryOptions(editor, options));

//#endregion
//#region src/internal/transforms/unwrapNodes.ts
const unwrapNodes$1 = (editor, options) => {
	unwrapNodes(editor, getQueryOptions(editor, options));
};

//#endregion
//#region src/internal/transforms/wrapNodes.ts
const wrapNodes$1 = (editor, element, { children, ...opt } = {}) => {
	const options = getQueryOptions(editor, opt);
	if (options.at) options.at = editor.api.unhangRange(options.at, options);
	if (children) {
		const path$2 = editor.api.path(options.at);
		if (!path$2) return;
		const node$2 = NodeApi.get(editor, path$2);
		if (!node$2?.children) return;
		editor.tf.withoutNormalizing(() => {
			const firstChildPath = PathApi.firstChild(path$2);
			wrapNodes(editor, element, {
				...options,
				at: firstChildPath
			});
			if (node$2.children.length > 1) editor.tf.moveNodes({
				at: path$2,
				children: true,
				fromIndex: 1,
				to: PathApi.child(firstChildPath, 1)
			});
		});
		return;
	}
	wrapNodes(editor, element, options);
};

//#endregion
//#region src/internal/transforms-extension/addMarks.ts
const addMarks = (editor, marks$1, { remove } = {}) => {
	if (!editor.selection) return;
	editor.tf.withoutNormalizing(() => {
		editor.tf.removeMarks([...castArray(remove), ...Object.keys(marks$1)]);
		Object.entries(marks$1).forEach(([key, value]) => {
			editor.tf.addMark(key, value);
		});
	});
};

//#endregion
//#region src/internal/transforms-extension/duplicateNodes.ts
const duplicateNodes = (editor, { block: block$1, nodes: nodes$1, ...options } = {}) => {
	const at = getAt(editor, options.at) ?? editor.selection;
	if (!nodes$1) return;
	const entries = nodes$1 ?? (at ? block$1 ? editor.api.blocks({ at }) : [] : []);
	if (entries.length === 0) return;
	const lastEntry = entries.at(-1);
	const insertPath = PathApi.next(lastEntry[1]);
	const nodesToInsert = entries.map(([node$2]) => node$2);
	editor.tf.insertNodes(nodesToInsert, {
		at: insertPath,
		...options
	});
};

//#endregion
//#region src/internal/transforms-extension/removeMarks.ts
const removeMarks = (editor, keys, { at, shouldChange = true, ...options } = {}) => {
	const selection = at ?? editor.selection;
	if (!selection) return;
	const match$1 = (node$2, path$2) => {
		if (!TextApi.isText(node$2)) return false;
		const [parentNode] = editor.api.parent(path$2);
		return !editor.api.isVoid(parentNode) || editor.api.markableVoid(parentNode);
	};
	const expandedSelection = RangeApi.isExpanded(selection);
	let markAcceptingVoidSelected = false;
	if (!expandedSelection) {
		const [selectedNode, selectedPath] = editor.api.node(selection);
		if (selectedNode && match$1(selectedNode, selectedPath)) {
			const [parentNode] = editor.api.parent(selectedPath);
			markAcceptingVoidSelected = parentNode && editor.api.markableVoid(parentNode);
		}
	}
	if (keys && (expandedSelection || markAcceptingVoidSelected)) {
		const props = castArray(keys);
		editor.tf.unsetNodes(props, {
			at: selection,
			match: match$1,
			split: true,
			voids: true,
			...options
		});
	} else if (!at) {
		const marks$1 = { ...editor.api.marks() };
		if (keys) {
			castArray(keys).forEach((k) => {
				delete marks$1[k];
			});
			editor.marks = marks$1;
		} else editor.marks = {};
		if (shouldChange) editor.api.onChange();
	}
};

//#endregion
//#region src/internal/transforms-extension/replaceNodes.ts
const replaceNodes = (editor, nodes$1, { at, children, removeNodes: removeOptions, ...options }) => {
	editor.tf.withoutNormalizing(() => {
		if (children) {
			if (!at) return;
			at = getAt(editor, at);
			const path$2 = editor.api.path(at);
			if (!path$2) return;
			editor.tf.removeNodes({
				...removeOptions,
				at: path$2,
				children: true
			});
			editor.tf.insertNodes(nodes$1, {
				...options,
				at: path$2.concat([0])
			});
		} else {
			editor.tf.removeNodes({
				...removeOptions,
				at
			});
			editor.tf.insertNodes(nodes$1, {
				...options,
				at
			});
		}
	});
};

//#endregion
//#region src/internal/transforms-extension/reset.ts
const reset = (editor, options = {}) => {
	editor.tf.replaceNodes(editor.api.create.value(), {
		at: [],
		children: true,
		...options
	});
	if (!options.children) {
		editor.operations = [];
		editor.marks = null;
		if (editor.history?.undos) {
			editor.history.undos = [];
			editor.history.redos = [];
		}
	}
};

//#endregion
//#region src/internal/transforms-extension/toggleBlock.ts
const toggleBlock = (editor, type, { defaultType: defaultTypeProp, someOptions, wrap, ...options } = {}) => {
	const at = options.at ?? editor.selection;
	if (!at) return;
	const isActive = editor.api.some({
		at,
		...someOptions,
		match: { type }
	});
	if (wrap) {
		if (isActive) editor.tf.unwrapNodes({
			at,
			match: { type }
		});
		else editor.tf.wrapNodes({
			children: [],
			type
		}, { at });
		return;
	}
	const defaultType = defaultTypeProp ?? editor.api.create.block().type ?? "p";
	if (isActive && type === defaultType) return;
	editor.tf.setNodes({ type: isActive ? defaultType : type }, {
		at,
		...options
	});
};

//#endregion
//#region src/internal/transforms-extension/toggleMark.ts
/** Add or remove mark in the selection. */
const toggleMark = (editor, key, { remove } = {}) => {
	if (!editor.selection) return;
	editor.tf.withoutNormalizing(() => {
		if (editor.api.hasMark(key)) {
			editor.tf.removeMark(key);
			return;
		}
		editor.tf.removeMarks([...castArray(remove), key]);
		editor.tf.addMark(key, true);
	});
};

//#endregion
//#region src/slate-history/history.ts
/** Weakmaps for attaching state to the editor. */
const SAVING = /* @__PURE__ */ new WeakMap();
const MERGING = /* @__PURE__ */ new WeakMap();
const SPLITTING_ONCE = /* @__PURE__ */ new WeakMap();
/** `HistoryApi` contains helpers for history-enabled editors. */
const HistoryApi = {
	isHistory(value) {
		return isPlainObject$1(value) && Array.isArray(value.redos) && Array.isArray(value.undos) && (value.redos.length === 0 || OperationApi.isOperationList(value.redos[0].operations)) && (value.undos.length === 0 || OperationApi.isOperationList(value.undos[0].operations));
	},
	isMerging(editor) {
		return MERGING.get(editor);
	},
	isSaving(editor) {
		return SAVING.get(editor);
	},
	isSplittingOnce(editor) {
		return SPLITTING_ONCE.get(editor);
	},
	redo(editor) {
		editor.redo();
	},
	setSplittingOnce(editor, value) {
		SPLITTING_ONCE.set(editor, value);
	},
	undo(editor) {
		editor.undo();
	},
	withMerging(editor, fn) {
		const prev = editor.api.isMerging();
		MERGING.set(editor, true);
		fn();
		MERGING.set(editor, prev);
	},
	withNewBatch(editor, fn) {
		const prev = editor.api.isMerging();
		MERGING.set(editor, true);
		SPLITTING_ONCE.set(editor, true);
		fn();
		MERGING.set(editor, prev);
		SPLITTING_ONCE.delete(editor);
	},
	withoutMerging(editor, fn) {
		const prev = editor.api.isMerging();
		MERGING.set(editor, false);
		fn();
		MERGING.set(editor, prev);
	},
	withoutSaving(editor, fn) {
		const prev = editor.api.isSaving();
		SAVING.set(editor, false);
		fn();
		SAVING.set(editor, prev);
	}
};

//#endregion
//#region src/create-editor.ts
const noop = (name, returnValue) => () => {
	console.warn(`[OVERRIDE_MISSING] The method editor.${name}() has not been implemented or overridden. This may cause unexpected behavior. Please ensure that all required editor methods are properly defined.`);
	return returnValue;
};
const createEditor = ({ children, selection } = {}) => {
	const editor = createEditor$1();
	if (children) editor.children = children;
	if (selection) editor.selection = selection;
	Object.assign(editor, {
		apply: bindFirst(apply, editor),
		isElementReadOnly: editor.isElementReadOnly,
		isInline: editor.isInline,
		isSelectable: editor.isSelectable,
		isVoid: editor.isVoid,
		markableVoid: editor.markableVoid,
		onChange: editor.onChange
	});
	Object.assign(editor, {
		addMark: bindFirst(addMark$1, editor),
		deleteBackward: bindFirst(deleteBackward$1, editor),
		deleteForward: bindFirst(deleteForward$1, editor),
		deleteFragment: bindFirst(deleteFragment$1, editor),
		getDirtyPaths: bindFirst(getDirtyPaths, editor),
		getFragment: bindFirst(getFragment$1, editor),
		insertBreak: bindFirst(insertBreak$1, editor),
		insertFragment: bindFirst(insertFragment$1, editor),
		insertNode: bindFirst(insertNode, editor),
		insertSoftBreak: bindFirst(insertSoftBreak, editor),
		insertText: bindFirst(insertText, editor),
		normalizeNode: bindFirst(normalizeNode$1, editor),
		removeMark: bindFirst(removeEditorMark, editor),
		shouldNormalize: bindFirst(shouldNormalize, editor)
	});
	Object.assign(editor, {
		above: bindFirst(above$1, editor),
		after: bindFirst(getPointAfter, editor),
		before: bindFirst(getPointBefore, editor),
		collapse: bindFirst(collapseSelection, editor),
		delete: bindFirst(deleteText, editor),
		deselect: bindFirst(deselect$1, editor),
		deselectDOM: bindFirst(deselectDOM, editor),
		edges: bindFirst(getEdgePoints, editor),
		elementReadOnly: bindFirst(isElementReadOnly, editor),
		end: bindFirst(getEndPoint, editor),
		first: bindFirst(getFirstNode, editor),
		fragment: bindFirst(getFragment$1, editor),
		getMarks: bindFirst(getMarks, editor),
		hasBlocks: bindFirst(hasBlocks$1, editor),
		hasInlines: bindFirst(hasInlines$1, editor),
		hasPath: bindFirst(hasPath, editor),
		hasTexts: bindFirst(hasTexts$1, editor),
		insertNodes: bindFirst(insertNodes$1, editor),
		isBlock: bindFirst(isBlock$1, editor),
		isEdge: bindFirst(isEdgePoint, editor),
		isEmpty: bindFirst(isEmpty$1, editor),
		isEnd: bindFirst(isEndPoint, editor),
		isNormalizing: bindFirst(isEditorNormalizing, editor),
		isStart: bindFirst(isStartPoint, editor),
		last: bindFirst(last$1, editor),
		leaf: bindFirst(getLeafNode, editor),
		levels: bindFirst(getLevels, editor),
		liftNodes: bindFirst(liftNodes$1, editor),
		mergeNodes: bindFirst(mergeNodes, editor),
		move: bindFirst(moveSelection, editor),
		moveNodes: bindFirst(moveNodes$1, editor),
		next: bindFirst(next, editor),
		node: bindFirst(node$1, editor),
		nodes: bindFirst(nodes, editor),
		normalize: bindFirst(normalizeEditor, editor),
		parent: bindFirst(parent$1, editor),
		path: bindFirst(path$1, editor),
		pathRef: bindFirst(createPathRef, editor),
		pathRefs: bindFirst(getPathRefs, editor),
		point: bindFirst(getPoint, editor),
		pointRef: bindFirst(createPointRef, editor),
		pointRefs: bindFirst(getPointRefs, editor),
		positions: bindFirst(getPositions, editor),
		previous: bindFirst(previous, editor),
		range: bindFirst(range$1, editor),
		rangeRef: bindFirst(createRangeRef, editor),
		rangeRefs: bindFirst(getRangeRefs, editor),
		removeNodes: bindFirst(removeNodes$1, editor),
		select: bindFirst(select$1, editor),
		setNodes: bindFirst(setNodes$1, editor),
		setNormalizing: bindFirst(setNormalizing, editor),
		setPoint: bindFirst(setPoint$1, editor),
		setSelection: bindFirst(setSelection$1, editor),
		shouldMergeNodes: bindFirst(shouldMergeNodes, editor),
		splitNodes: bindFirst(splitNodes$1, editor),
		start: bindFirst(getStartPoint, editor),
		string: bindFirst(getEditorString, editor),
		unhangRange: bindFirst(unhangRange$1, editor),
		unsetNodes: bindFirst(unsetNodes$1, editor),
		unwrapNodes: bindFirst(unwrapNodes$1, editor),
		void: bindFirst(getVoidNode, editor),
		withoutNormalizing: bindFirst(withoutNormalizing$1, editor),
		wrapNodes: bindFirst(wrapNodes$1, editor)
	});
	Object.assign(editor, {
		history: {
			redos: [],
			undos: []
		},
		meta: { isNormalizing: false },
		redo: noop("redo"),
		undo: noop("undo"),
		writeHistory: noop("writeHistory")
	});
	Object.assign(editor, {
		insertData: noop("insertData"),
		insertFragmentData: noop("insertFragmentData", false),
		insertTextData: noop("insertTextData", false),
		setFragmentData: noop("setFragmentData")
	});
	const api = {
		block: bindFirst(block, editor),
		blocks: bindFirst(blocks, editor),
		create: {
			block: (props) => ({
				children: [{ text: "" }],
				type: "p",
				...props
			}),
			value: () => [api.create.block()]
		},
		descendant: bindFirst(descendant, editor),
		edgeBlocks: bindFirst(edgeBlocks, editor),
		findDocumentOrShadowRoot: bindFirst(findDocumentOrShadowRoot, editor),
		findEventRange: bindFirst(findEventRange, editor),
		findKey: bindFirst(findKey, editor),
		findPath: bindFirst(findPath, editor),
		getWindow: bindFirst(getWindow, editor),
		hasDOMNode: bindFirst(hasDOMNode, editor),
		hasEditableTarget: bindFirst(hasEditableTarget, editor),
		hasMark: bindFirst(hasMark, editor),
		hasRange: bindFirst(hasRange, editor),
		hasSelectableTarget: bindFirst(hasSelectableTarget, editor),
		hasTarget: bindFirst(hasTarget, editor),
		isAt: bindFirst(isAt, editor),
		isComposing: bindFirst(isComposing, editor),
		isEditorEnd: bindFirst(isEditorEnd, editor),
		isFocused: bindFirst(isFocused, editor),
		isMerging: bindFirst(HistoryApi.isMerging, editor),
		isReadOnly: bindFirst(isReadOnly, editor),
		isSaving: bindFirst(HistoryApi.isSaving, editor),
		isSelected: bindFirst(isSelected, editor),
		isSplittingOnce: bindFirst(HistoryApi.isSplittingOnce, editor),
		isTargetInsideNonReadonlyVoid: bindFirst(isTargetInsideNonReadonlyVoid, editor),
		isText: bindFirst(isText, editor),
		mark: bindFirst(mark, editor),
		nodesRange: bindFirst(nodesRange, editor),
		prop,
		scrollIntoView: bindFirst(scrollIntoView, editor),
		some: bindFirst(some, editor),
		toDOMNode: bindFirst(toDOMNode, editor),
		toDOMPoint: bindFirst(toDOMPoint, editor),
		toDOMRange: bindFirst(toDOMRange, editor),
		toSlateNode: bindFirst(toSlateNode, editor),
		toSlatePoint: bindFirst(toSlatePoint, editor),
		toSlateRange: bindFirst(toSlateRange, editor),
		isCollapsed: () => RangeApi.isCollapsed(editor.selection),
		isExpanded: () => RangeApi.isExpanded(editor.selection),
		shouldNormalizeNode: () => true
	};
	const transforms = {
		addMarks: bindFirst(addMarks, editor),
		blur: bindFirst(blur, editor),
		deselectDOM: bindFirst(deselectDOM, editor),
		duplicateNodes: bindFirst(duplicateNodes, editor),
		focus: bindFirst(focus, editor),
		removeMarks: bindFirst(removeMarks, editor),
		replaceNodes: bindFirst(replaceNodes, editor),
		reset: bindFirst(reset, editor),
		setSplittingOnce: bindFirst(HistoryApi.setSplittingOnce, editor),
		toggleBlock: bindFirst(toggleBlock, editor),
		toggleMark: bindFirst(toggleMark, editor),
		withMerging: bindFirst(HistoryApi.withMerging, editor),
		withNewBatch: bindFirst(HistoryApi.withNewBatch, editor),
		withoutMerging: bindFirst(HistoryApi.withoutMerging, editor),
		withoutSaving: bindFirst(HistoryApi.withoutSaving, editor),
		escape: () => false,
		moveLine: () => false,
		selectAll: () => false,
		tab: () => false
	};
	editor.api = api;
	editor.tf = transforms;
	editor.transforms = transforms;
	syncLegacyMethods(editor);
	return editor;
};

//#endregion
//#region src/slate-history/with-history.ts
/**
* The `withHistory` plugin keeps track of the operation history of a Slate
* editor as operations are applied to it, using undo and redo stacks.
*/
const withHistory = (editor) => {
	const e$2 = editor;
	const { apply: apply$1 } = e$2;
	e$2.history = {
		redos: [],
		undos: []
	};
	e$2.redo = () => {
		const { history } = e$2;
		const { redos } = history;
		if (redos.length > 0) {
			const batch = redos.at(-1);
			if (batch.selectionBefore) e$2.tf.setSelection(batch.selectionBefore);
			e$2.tf.withoutSaving(() => {
				e$2.tf.withoutNormalizing(() => {
					for (const op of batch.operations) e$2.apply(op);
				});
			});
			history.redos.pop();
			e$2.writeHistory("undos", batch);
		}
	};
	e$2.undo = () => {
		const { history } = e$2;
		const { undos } = history;
		if (undos.length > 0) {
			const batch = undos.at(-1);
			e$2.tf.withoutSaving(() => {
				e$2.tf.withoutNormalizing(() => {
					const inverseOps = batch.operations.map(OperationApi.inverse).reverse();
					for (const op of inverseOps) e$2.apply(op);
					if (batch.selectionBefore) e$2.tf.setSelection(batch.selectionBefore);
				});
			});
			e$2.writeHistory("redos", batch);
			history.undos.pop();
		}
	};
	e$2.apply = (op) => {
		const { history, operations } = e$2;
		const { undos } = history;
		const lastBatch = undos.at(-1);
		const lastOp = lastBatch?.operations.at(-1);
		let save = e$2.api.isSaving();
		let merge = e$2.api.isMerging();
		if (save == null) save = shouldSave(op, lastOp);
		if (save) {
			if (merge == null) if (lastBatch == null) merge = false;
			else if (operations.length > 0) merge = true;
			else merge = shouldMerge(op, lastOp);
			if (e$2.api.isSplittingOnce()) {
				merge = false;
				e$2.tf.setSplittingOnce(void 0);
			}
			if (lastBatch && merge) lastBatch.operations.push(op);
			else {
				const batch = {
					operations: [op],
					selectionBefore: e$2.selection
				};
				e$2.writeHistory("undos", batch);
			}
			while (undos.length > 100) undos.shift();
			history.redos = [];
		}
		apply$1(op);
	};
	e$2.writeHistory = (stack, batch) => {
		e$2.history[stack].push(batch);
	};
	return e$2;
};
/** Check whether to merge an operation into the previous operation. */
const shouldMerge = (op, prev) => {
	if (prev && op.type === "insert_text" && prev.type === "insert_text" && op.offset === prev.offset + prev.text.length && PathApi.equals(op.path, prev.path)) return true;
	if (prev && op.type === "remove_text" && prev.type === "remove_text" && op.offset + op.text.length === prev.offset && PathApi.equals(op.path, prev.path)) return true;
	return false;
};
/** Check whether an operation needs to be saved to the history. */
const shouldSave = (op, _) => {
	if (op.type === "set_selection") return false;
	return true;
};

//#endregion
export { CAN_USE_DOM, ElementApi, HAS_BEFORE_INPUT_SUPPORT, HistoryApi, IS_ANDROID, IS_CHROME, IS_FIREFOX, IS_FIREFOX_LEGACY, IS_IOS, IS_UC_MOBILE, IS_WEBKIT, IS_WECHATBROWSER, LocationApi, NodeApi, OperationApi, PathApi, PathRefApi, PointApi, PointRefApi, RangeApi, RangeRefApi, SpanApi, TRIPLE_CLICK, TextApi, applyStringDiff, assignLegacyApi, assignLegacyTransforms, combineMatch, combineMatchOptions, combineTransformMatchOptions, createEditor, deleteMerge, getActiveElement, getAt, getDefaultView, getMatch, getQueryOptions, getSelection, hasShadowRoot, isAfter, isBefore, isDOMElement, isDOMNode, isDOMSelection, isElementDecorationsEqual, isPlainTextOnlyPaste, isTextDecorationsEqual, isTrackedMutation, match, mergeStringDiffs, normalizeDOMPoint, normalizePoint, normalizeRange, normalizeStringDiff, queryEditor, queryNode, syncLegacyMethods, targetRange, verifyDiffState, withDOM, withHistory };